import { baseApi } from './baseApi';

/**
 * Known cross-entity relations:
 * When a mutation occurs on entity Key, all related entities in Value
 * will also be automatically invalidated.
 */
export const RESOURCE_RELATIONS: Record<string, string[]> = {
	// Sales relations
	'api/client-orders': ['api/stocks', 'api/ordered-products'],
	'api/client-delivery-notes': ['api/client-orders', 'api/stocks', 'api/client-invoices'],
	'api/client-invoices': ['api/client-delivery-notes', 'api/client-payments', 'api/client-issued-returns'],
	'api/client-payments': ['api/client-invoices', 'api/client-financial-returns'],
	'api/client-financial-returns': ['api/client-invoices', 'api/client-payments'],
	'api/client-issued-returns': ['api/client-invoices', 'api/stocks'],

	// Purchases relations
	'api/supplier-purchase-orders': ['api/stocks'],
	'api/supplier-delivery-notes': ['api/supplier-purchase-orders', 'api/stocks', 'api/supplier-invoices'],
	'api/supplier-invoices': ['api/supplier-delivery-notes', 'api/supplier-payments', 'api/supplier-received-returns'],
	'api/supplier-payments': ['api/supplier-invoices', 'api/supplier-financial-assets'],
	'api/supplier-received-returns': ['api/supplier-invoices', 'api/stocks'],

	// Stock relations
	'api/stocks': ['api/products'],
	'api/stock-transfers': ['api/stocks'],
	'api/inventories': ['api/stocks'],
};

/**
 * Endpoints that should not trigger automatic table cache invalidation.
 */
const SKIP_PATTERNS = [/api\/auth/i, /api\/reports\/marketplace/i];

let storeInstance: any = null;

/**
 * Set the store reference used for dispatching RTK Query tag invalidations.
 * Typically called during store initialization.
 */
export const setInvalidationStore = (store: any): void => {
	storeInstance = store;
};

/**
 * Get current store instance.
 */
export const getInvalidationStore = (): any => storeInstance;

/**
 * Cleans and normalizes a URL by stripping:
 * - Domain / protocol
 * - Query strings and hashes
 * - Leading and trailing slashes
 */
export const normalizeUrl = (url?: string): string => {
	if (!url) return '';
	return url
		.replace(/^https?:\/\/[^/]+/i, '')
		.split('#')[0]
		.split('?')[0]
		.replace(/^\/+|\/+$/g, '');
};

/**
 * Checks if a path segment represents an ID (integer, UUID, or Mongo ObjectId).
 */
export const isIdSegment = (segment: string): boolean => {
	if (!segment) return false;
	const isNumeric = /^\d+$/.test(segment);
	const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment);
	const isMongoId = /^[0-9a-f]{24}$/i.test(segment);
	return isNumeric || isUuid || isMongoId;
};

/**
 * Extracts the base resource root from an API URL.
 * Examples:
 *   - 'api/clients/120/activation' -> 'api/clients'
 *   - 'api/client-orders/123/status' -> 'api/client-orders'
 *   - 'api/params/clients/categories' -> 'api/params/clients/categories'
 *   - 'connection-history' -> 'connection-history'
 */
export const getBaseResource = (url: string): string => {
	const clean = normalizeUrl(url);
	if (!clean) return '';

	const segments = clean.split('/');

	if (segments[0] === 'api') {
		if (segments.length === 1) return 'api';

		// Nested namespaces: 'params' or 'reports'
		if (segments[1] === 'params' && segments.length >= 4) {
			return segments.slice(0, 4).join('/');
		}
		if (segments[1] === 'reports' && segments.length >= 3) {
			return segments.slice(0, 3).join('/');
		}

		return `api/${segments[1]}`;
	}

	return segments[0];
};

/**
 * Generates all RTK Query cache tags provided by a table query based on its URL.
 */
export const getQueryCacheTags = (url: string): string[] => {
	const clean = normalizeUrl(url);
	if (!clean) return [];

	const tags = new Set<string>();
	const base = getBaseResource(clean);
	if (base) tags.add(base);

	// Also add the exact cleaned path
	tags.add(clean);

	// If path contains IDs (e.g. 'api/suppliers/42/contacts'), add normalized wildcard pattern
	const segments = clean.split('/');
	if (segments.some(isIdSegment)) {
		const pattern = segments.map((seg) => (isIdSegment(seg) ? ':id' : seg)).join('/');
		tags.add(pattern);
	}

	return Array.from(tags);
};

/**
 * Determines all cache tags that should be invalidated by a mutation URL.
 */
export const getMutationInvalidationTags = (url: string, extraTags?: string[]): string[] => {
	const clean = normalizeUrl(url);
	if (!clean) return extraTags ? [...extraTags] : [];

	// Check if this URL pattern should be skipped
	if (SKIP_PATTERNS.some((pattern) => pattern.test(clean))) {
		return extraTags ? [...extraTags] : [];
	}

	const tags = new Set<string>();

	// 1. Add base resource
	const base = getBaseResource(clean);
	if (base) {
		tags.add(base);

		// 2. Add related resources if any
		const related = RESOURCE_RELATIONS[base];
		if (related) {
			for (const r of related) {
				tags.add(r);
			}
		}
	}

	// 3. If nested sub-resource with ID (e.g. 'api/suppliers/42/contacts' or 'api/suppliers/42/contacts/5')
	const segments = clean.split('/');
	if (segments.length > 2) {
		// Filter out trailing ID or status/action segment
		const nonIdSegments = segments.filter((seg) => !isIdSegment(seg));
		if (nonIdSegments.length > 2) {
			tags.add(nonIdSegments.join('/'));
		}

		// Also add wildcard pattern with :id
		const pattern = segments
			.filter((seg, idx) => idx !== segments.length - 1 || !isIdSegment(seg))
			.map((seg) => (isIdSegment(seg) ? ':id' : seg))
			.join('/');
		if (pattern) {
			tags.add(pattern);
		}
	}

	// 4. Add extra caller-specified tags
	if (extraTags) {
		for (const tag of extraTags) {
			if (tag) tags.add(tag);
		}
	}

	return Array.from(tags);
};

/**
 * Manually invalidates specific TableData tags in RTK Query.
 */
export const invalidateTableCache = (tags: string | string[]): void => {
	const tagArray = Array.isArray(tags) ? tags : [tags];
	if (tagArray.length === 0) return;

	const store = getInvalidationStore();
	if (store?.dispatch) {
		const rtkTags = tagArray.map((id) => ({ type: 'TableData' as const, id }));
		store.dispatch(baseApi.util.invalidateTags(rtkTags));
	}
};

/**
 * Dispatches RTK Query cache invalidations when a mutation completes successfully.
 */
export const invalidateOnMutation = (url: string, extraTags?: string[], skipInvalidation?: boolean): void => {
	if (skipInvalidation) return;

	try {
		const tagsToInvalidate = getMutationInvalidationTags(url, extraTags);
		if (tagsToInvalidate.length === 0) return;

		const store = getInvalidationStore();
		if (store?.dispatch) {
			const rtkTags = tagsToInvalidate.map((id) => ({ type: 'TableData' as const, id }));
			store.dispatch(baseApi.util.invalidateTags(rtkTags));
		}
	} catch (err) {
		// Defensive: never let an invalidation failure break application flow
		console.warn('Failed to invalidate table cache tags:', err);
	}
};
